import { useContext, useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { toast } from 'react-toastify';
import { AppContext } from '../../../../common';
import { Modal } from '../../../../common/components/modals';
import { ACTIONS } from '../../../../constants';
import { CoverRequest } from '../../../../models/coverings/CoverRequest';
import { SchedulingChangeRequestsRequest } from '../../../../models/employees-portal/CreateSchedulingRequest';
import { CoveringsAPI } from '../../../../services/external/APIs/coverings/Coverings.api';
import { getMonths, handlerFormatDate } from '../../../../utils';
import { getActionService } from '../../../../utils/getActionService';
import { TProps } from '../../home/HomeEmployeesPortal';
import { useRequest } from '../../hooks/useRequest';
import { CoverSchedulingByEmployeeAndDate } from '../../models/coverScheduling';
import { WorkshiftSelector } from '../forms/selectors/WorkshiftSelector';
import { InterruptionRequestModal } from './InterruptionRequestModal';
import { RequestCoveringsFormModal } from './RequestCoveringsFormModal';

const COVER_CONTROL_WITH_RESTRICTIONS_BY_SHIFT = 'C';

const parseApplicationParameterBoolean = (value: string | number | null | undefined): boolean => {
  if (value == null) {
    return false;
  }

  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
};

interface RequestModalProps {
  isOpen: boolean;
  onClose: (newState: boolean) => void;
  requestModalInfo: TProps | undefined;
}

export const RequestModal = ({ isOpen, onClose, requestModalInfo }: RequestModalProps) => {
  const {
    lang,
    isSkinSmart,
    langResources,
    interruptionTypesList,
    userInfo,
    parametersApplication,
  } = useContext(AppContext);

  const coverControlWithRestrictionsByShift = useMemo(() => {
    const parameter = parametersApplication.find(
      (p) => p.name === COVER_CONTROL_WITH_RESTRICTIONS_BY_SHIFT,
    );

    return parseApplicationParameterBoolean(parameter?.value);
  }, [parametersApplication]);

  const { createRequest, CreateCoverRequest, isLoading } = useRequest();

  const [request, setRequest] = useState<SchedulingChangeRequestsRequest>({
    endDate: requestModalInfo ? requestModalInfo.startDate.replaceAll('/', '-').split('T')[0] : '',
    startDate: requestModalInfo
      ? requestModalInfo.startDate.replaceAll('/', '-').split('T')[0]
      : '',
    workshiftSchedulingChangeRequest: {
      comments: '',
      workshiftId: 0,
    },
    interruptionRequest: {
      file: null,
      comments: '',
      date: requestModalInfo ? requestModalInfo.startDate.replaceAll('/', '-').split('T')[0] : '',
      endHour: requestModalInfo ? requestModalInfo.endHour.split('.')[0] : '',
      interruptionTypeId: 0,
      startHour: requestModalInfo ? requestModalInfo.startHour.split('.')[0] : '',
      isFullDay: false,
    },
  });
  const [requestType, setRequestType] = useState(requestModalInfo ? requestModalInfo.type : '');
  const [startDate, setStartDate] = useState(
    requestModalInfo ? requestModalInfo.startDate.split('T')[0] : '',
  );
  const [endDate, setEndDate] = useState(
    requestModalInfo ? requestModalInfo.endDate.split('T')[0] : '',
  );

  const [coverPerson, setCoverPerson] = useState<{
    id: number;
    fullName: string;
  }>();
  const [coverScheduling, setCoverScheduling] = useState<CoverSchedulingByEmployeeAndDate>();

  const [coverRequest, setCoverRequest] = useState<CoverRequest>({} as CoverRequest);

  const [isRequireLongShift, setIsRequireLongShift] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handlerSetWholeDay = (value: boolean, interruptionId?: number) => {
    const intId = interruptionId ? interruptionId : request.interruptionRequest.interruptionTypeId;

    const interruption = interruptionTypesList.find((i) => i.id == intId);

    let defaultInitialHourDay = '07:00';
    let defaultFinalHourDay = '15:00';

    if (interruption?.defaultTime) {
      let finalHourValue = new Date(interruption.defaultTime).getHours();
      let finalMinuteValue = new Date(interruption.defaultTime).getMinutes();

      if (finalHourValue == 0 && finalMinuteValue == 0) {
        let date: SchedulingChangeRequestsRequest = {
          ...request,
          interruptionRequest: {
            ...request.interruptionRequest,
            date: request.interruptionRequest.date.split('T')[0],
            endHour: '',
            startHour: '',
            isFullDay: value,
          },
        };

        setRequest(date);

        setErrorMessage(langResources['msg.invalid.validation.defaultTime']);
      } else {
        const [horaInicialHoras, horaInicialMinutos] = defaultInitialHourDay.split(':').map(Number);

        finalHourValue += horaInicialHoras;
        finalMinuteValue += horaInicialMinutos;

        const horaFinal = new Date(interruption.defaultTime);

        horaFinal.setHours(finalHourValue, finalMinuteValue);

        defaultFinalHourDay = horaFinal.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

        let date: SchedulingChangeRequestsRequest = {
          ...request,
          interruptionRequest: {
            ...request.interruptionRequest,
            endHour: defaultFinalHourDay + ':00',
            startHour: defaultInitialHourDay + ':00',
            isFullDay: value,
          },
        };

        setRequest(date);
      }
    } else {
      let date: SchedulingChangeRequestsRequest = {
        ...request,
        interruptionRequest: {
          ...request.interruptionRequest,
          date: request.interruptionRequest.date.split('T')[0],
          endHour: '',
          startHour: '',
          isFullDay: value,
        },
      };

      setRequest(date);
    }
  };

  const handlerClose = () => {
    onClose(false);
  };

  const handlerSubitCoverings = () => {
    let id = toast.loading('Creando solicitud de cubrimiento...');

    CreateCoverRequest(coverRequest, request, coverPerson, requestModalInfo, coverScheduling)
      .then((res) => {
        setErrorMessage('');
        toast.update(id, {
          render: 'Se creó correctamente',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
        onClose(false);
      })
      .catch((error) => {
        const errorMsg =
          error?.message ||
          error?.response?.data?.message ||
          error?.request?.response ||
          'Ocurrió un error al crear la solicitud de cubrimientos';
        setErrorMessage(errorMsg);
        toast.update(id, {
          render: errorMsg,
          type: 'error',
          isLoading: false,
          autoClose: 5000,
          position: toast.POSITION.BOTTOM_RIGHT,
          closeOnClick: true,
          closeButton: true,
        });
      });
  };

  const hanlderSubmit = () => {
    if (requestType === 'coverings') {
      handlerSubitCoverings();
      return;
    }

    let id = toast.loading('Creando solicitud de cambio...');

    createRequest(request)
      .then((res) => {
        if (res.request.status == 400) {
          setErrorMessage(res?.request?.response);
          toast.update(id, {
            render: res?.request?.response,
            isLoading: false,
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT,
            closeOnClick: true,
            type: 'error',
            closeButton: true,
          });
        } else if (res.length > 0) {
          toast.update(id, {
            render:
              'Para las siguientes fechas no es posible hacer el cambio ' +
              res.map((e: string) => e.split('T')[0]).join(),
            type: 'warning',
            isLoading: false,
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT,
            closeOnClick: true,
            closeButton: true,
          });
        } else {
          toast.update(id, {
            render: 'Se creó la solicitud exitosamente',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT,
            closeOnClick: true,
            closeButton: true,
          });
        }
        onClose(false);
      })
      .catch((err) => {
        setErrorMessage(err?.request?.response);

        if (err.errponse && typeof err.response.data === 'string') {
          toast.update(id, {
            render: err.response.data,
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            position: toast.POSITION.BOTTOM_RIGHT,
            closeOnClick: true,
            closeButton: true,
          });
        }
      });
  };

  const handleInputChangeWorkshift = (name: string, value: string | number | null | undefined) => {
    let data = { ...request };
    let workshiftsCopy = {
      ...data.workshiftSchedulingChangeRequest,
      [name]: value,
    };

    data.workshiftSchedulingChangeRequest = workshiftsCopy;
    setRequest(data);
  };

  const handleInputInterruption = (name: string, value: any) => {
    let data = { ...request };
    let interruptionsCopy = { ...data.interruptionRequest, [name]: value };

    if (name === 'date' && typeof value === 'string') {
      data.startDate = value;
      data.endDate = value;
    }

    data.interruptionRequest = interruptionsCopy;
    setRequest(data);
  };

  /**
   * Cambia las fechas inicial y final
   * @param dates
   */
  const handlerChangeDate = (dates: Date[]) => {
    if (!dates || dates.length < 2) {
      return;
    }

    let initDate = handlerFormatDate(dates[0]);
    let lastDate = handlerFormatDate(dates[1]);

    let data: SchedulingChangeRequestsRequest = {
      ...request,
      startDate: initDate,
      endDate: lastDate,
    };

    setRequest(data);
  };

  useEffect(() => {
    if (requestType == '') {
      setErrorMessage('Selecciona el tipo de solicitud');
    } else if (
      requestType == 'workshift' &&
      request.workshiftSchedulingChangeRequest.comments.length > 0 &&
      request.workshiftSchedulingChangeRequest.workshiftId > 0 &&
      startDate <= endDate
    ) {
      setErrorMessage('');
    } else if (
      requestType == 'interruption' &&
      request.interruptionRequest.interruptionTypeId > 0 &&
      request.interruptionRequest.comments.length > 0 &&
      request.interruptionRequest.date.length > 0 &&
      request.interruptionRequest.startHour !== '' &&
      request.interruptionRequest.endHour !== ''
    ) {
      if (request.interruptionRequest.startHour > request.interruptionRequest.endHour) {
        setErrorMessage('La hora incial debe ser menor que la final');
      } else {
        setErrorMessage('');
      }
    } else {
      setErrorMessage('Ingresa todos los campos correctamente');
    }
  }, [requestType, request, startDate, endDate]);

  const handlerChangeCoverPerson = (model: { id: number; fullName: string }) => {
    setCoverPerson(model);
  };

  const handlerInputChangeCover = (name: string, value: string | null | number | undefined) => {
    setCoverRequest({ ...coverRequest, [name]: value });
  };

  useEffect(() => {
    let errors = []; // Acumulamos los errores en un array.

    if (requestType == 'coverings') {
      if (
        coverPerson == undefined ||
        requestModalInfo?.info == undefined ||
        coverRequest.comments === '' ||
        coverRequest.coverPersonId
      ) {
        errors.push('Debes ingresar todos los campos correctamente');
      }

      const isCompensationDateExempt =
        !(coverScheduling?.workshiftId != null && coverRequest.interruptionTypeRequestId == null) &&
        coverRequest.compensationDate == null;

      if (coverControlWithRestrictionsByShift && !isCompensationDateExempt) {
        errors.push('Debes ingresar una fecha de compensación');
      }

      if (
        coverControlWithRestrictionsByShift &&
        isRequireLongShift &&
        coverRequest.workshiftIdToBeCompensated == null &&
        coverRequest.workshiftRequestId == null
      ) {
        errors.push('Debes selecionar un turno corrido');
      }
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  }, [
    coverRequest,
    coverScheduling,
    request,
    requestModalInfo,
    isRequireLongShift,
    coverControlWithRestrictionsByShift,
  ]);

  useEffect(() => {
    if (coverPerson)
      CoveringsAPI.getSchedulingByEmplooye({
        date: request?.interruptionRequest.date.split('T')[0],
        employeeId: coverPerson.id,
      }).then((res: CoverSchedulingByEmployeeAndDate) => {
        if (!coverControlWithRestrictionsByShift) {
          setCoverScheduling(res);
        } else if (
          (res.interruptionTypeId && res.interruptionTypeName) ||
          (res.workshiftId && res.workshiftDescription)
        ) {
          setCoverScheduling(res);
        } else {
          setErrorMessage('Para este día no hay una programación, intenta con otro día');
        }
      });
  }, [coverPerson]);

  const isWorkshiftForWorkshift =
    requestModalInfo?.info?.workshiftId &&
    coverScheduling?.workshiftId &&
    !coverRequest.interruptionTypeRequestId;

  const isCompensationDateExempt = coverControlWithRestrictionsByShift && isWorkshiftForWorkshift;

  const isControlValidations = () => {
    if (coverControlWithRestrictionsByShift) {
      return (
        isLoading ||
        errorMessage.length > 0 ||
        (requestType == 'coverings' && !isCompensationDateExempt && !coverRequest.compensationDate)
      );
    } else {
      return isLoading || errorMessage.length > 0;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={'Solicitar Cambios'}
      onClose={handlerClose}
      onSubmit={hanlderSubmit}
      disableSubmit={isControlValidations()}
    >
      <div className="modal-body text-black">
        <form action="">
          <div>
            <p className="">¿Qué cambio deseas solicitar?</p>

            <div className="d-flex justify-content-around align-items-center flex-wrap">
              {getActionService(userInfo.actions, ACTIONS['approved.scheduling']) && (
                <div className="  checked-custom">
                  <input
                    className="form-check-input me-1"
                    type="radio"
                    name="marking-event-type"
                    id="flexRadioDefault1"
                    checked={requestType === 'workshift'}
                    onChange={(e) => setRequestType(e.target.value)}
                    value={'workshift'}
                  />
                  <label className="form-check-label" htmlFor="flexRadioDefault1">
                    Turno
                  </label>
                </div>
              )}

              {getActionService(userInfo.actions, ACTIONS['approve.interruptions']) && (
                <div className="  checked-custom">
                  <input
                    className="form-check-input me-1"
                    type="radio"
                    name="marking-event-type"
                    id="flexRadioDefault2"
                    checked={requestType === 'interruption'}
                    onChange={(e) => setRequestType(e.target.value)}
                    value={'interruption'}
                  />
                  <label className="form-check-label" htmlFor="flexRadioDefault2">
                    Permisos
                  </label>
                </div>
              )}

              {getActionService(userInfo.actions, ACTIONS['approve.covers']) &&
              //@ts-ignore
              (requestModalInfo?.info?.workshiftId || requestModalInfo?.info?.interruptionId) ? (
                <div className="  checked-custom">
                  <input
                    className="form-check-input me-1"
                    type="radio"
                    name="marking-event-type"
                    id="flexRadioDefault3"
                    checked={requestType === 'coverings'}
                    onChange={(e) => setRequestType(e.target.value)}
                    value={'coverings'}
                  />
                  <label className="form-check-label" htmlFor="flexRadioDefault3">
                    Cubrimientos
                  </label>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-3">
            {['workshift'].includes(requestType) && (
              <div>
                <h5>Turnos</h5>
                <div className="row g-3">
                  <div className=" col-12 col-md-6">
                    <label className="form-label" htmlFor="">
                      Fecha Inicial/Fecha Final *
                    </label>

                    <div className="date__picker custom">
                      <Flatpickr
                        lang="es"
                        title={startDate + ' to ' + endDate}
                        id="picker-date"
                        className="form-control picker text-black select__control"
                        options={{
                          mode: 'range',
                          dateFormat: 'Y-m-d',
                          locale: {
                            months: getMonths(lang),
                          },
                        }}
                        onChange={(date) => {
                          handlerChangeDate(date);
                        }}
                        value={request.startDate + ' to ' + request.endDate}
                        placeholder="YYYY-MM-DD"
                      />
                      <label htmlFor="picker-date">
                        <i className="mdi mdi-calendar-blank fs-4 text-muted"></i>
                      </label>
                    </div>
                  </div>

                  <div className="form-group mb-3  col-12 col-md-6">
                    <label className="form-label" htmlFor="">
                      Seleccione Turno *
                    </label>

                    <WorkshiftSelector
                      name="workshiftId"
                      onChange={handleInputChangeWorkshift}
                      value={request?.workshiftSchedulingChangeRequest.workshiftId}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="">
                    Escribe el motivo de la solicitud*
                  </label>

                  <textarea
                    value={request?.workshiftSchedulingChangeRequest.comments}
                    onChange={(e) => handleInputChangeWorkshift('comments', e.target.value)}
                    className="form-control"
                    name=""
                    id=""
                    cols={20}
                    rows={5}
                  ></textarea>
                </div>
              </div>
            )}

            {['interruption'].includes(requestType) && (
              <InterruptionRequestModal
                handleInputInterruption={handleInputInterruption}
                request={request}
                handlerSetWholeDay={handlerSetWholeDay}
                isDateLocked={requestModalInfo?.isDateLocked}
              />
            )}

            {['coverings'].includes(requestType) && (
              <RequestCoveringsFormModal
                coverRequest={coverRequest}
                coverScheduling={coverScheduling}
                request={request}
                requestModalInfo={requestModalInfo}
                handlerInputChangeCover={handlerInputChangeCover}
                handleInputInterruption={handleInputInterruption}
                handlerChangeCoverPerson={handlerChangeCoverPerson}
                setErrorMessage={setErrorMessage}
                setCoverRequest={setCoverRequest}
                errorMessage={errorMessage}
                setIsRequireLongShift={setIsRequireLongShift}
                isRequireLongShift={isRequireLongShift}
              />
            )}
          </div>

          <p className="py-2  ">
            {errorMessage && errorMessage?.split(',').length > 0 ? (
              <div className="alert alert-danger mb-0" role="alert">
                {errorMessage.split(',').map((error, i) => (
                  <p className="mb-0" key={i}>
                    {error}
                  </p>
                ))}
              </div>
            ) : (
              errorMessage && (
                <div className="alert alert-danger mb-0" role="alert">
                  {errorMessage}
                </div>
              )
            )}
          </p>
        </form>
      </div>
    </Modal>
  );
};
